// last modified by AI tools on 2026-09-30
/**
 * useElectionDetailsState Composable
 *
 * Shared state management for Election Details panels (mobile + desktop).
 * Extracts all reactive state, data fetching, filtering, and chart logic
 * previously embedded in ElectionDetailsPanel.vue.
 */
import { ref, computed, watch, type MaybeRefOrGetter, toValue } from 'vue';
import type { ForecastTraderElectionMapProduct } from '@/types/forecasttrader.types';
import { PARTY_COLORS, type PartyCode } from '../types/election.types';
import { useElectionProductSelector } from './useElectionProductSelector';
import { useCandidateDetails } from './useCandidateDetails';
import { filterTopTwoCandidates } from '../utils/leadingCandidate';

/** Map party code to dark color for chart series */
function getPartyColor(party: PartyCode): string {
  const colorMap: Record<PartyCode, string> = {
    DEM: PARTY_COLORS.DEM_DARK,
    GOP: PARTY_COLORS.GOP_DARK,
    IND: PARTY_COLORS.IND_DARK,
    GRN: PARTY_COLORS.GRN_DARK,
  };
  return colorMap[party] ?? PARTY_COLORS.NEUTRAL_DARK;
}

export type DetailViewMode = 'multi-candidate' | 'single-candidate';

export interface UseElectionDetailsStateOptions {
  /** Reactive array of available election products for this state */
  products: MaybeRefOrGetter<ForecastTraderElectionMapProduct[]>;
  /** Initial product conid for pre-selecting dropdown */
  initialProductConid?: MaybeRefOrGetter<number | undefined>;
  /** Initial candidate conid for opening single-candidate view */
  initialCandidateConid?: MaybeRefOrGetter<number | undefined>;
  /** Reactive visibility flag — used to reset state on open/close */
  visible: MaybeRefOrGetter<boolean>;
  /** Leading party from API ('Democrat', 'Republican', etc.) */
  party?: MaybeRefOrGetter<string | undefined>;
  /** Color intensity from API (0=toss-up, 1=lean, 2=likely, 3=solid) */
  color?: MaybeRefOrGetter<number | undefined>;
}

export function useElectionDetailsState(options: UseElectionDetailsStateOptions) {
  const { products, initialProductConid, visible } = options;

  // ── Product selector & data fetching ──
  const {
    selectedProduct,
    availableProducts,
    topOIData,
    electionContract,
    actualCandidates,
    productConids,
    isLoadingContracts,
    contractError,
    marketInfo,
  } = useElectionProductSelector(products, initialProductConid);

  // ── Local UI state ──
  const chartMode = ref<'line' | 'area'>('line');
  const chartModeOptions = [
    { label: 'Line', value: 'line' },
    { label: 'Area', value: 'area' },
  ] as const;

  const chartPeriod = ref('1w');
  const chartPeriodOptions = [
    { label: '1D', value: '1d' },
    { label: '1W', value: '1w' },
    { label: '1M', value: '1m' },
  ] as const;
  const chartPeriodMap: Record<string, { period: string; step: string }> = {
    '1d': { period: '1day', step: '5mins' },
    '1w': { period: '1week', step: '30mins' },
    '1m': { period: '1month', step: '1hour' },
  };
  const chartPeriodConfig = computed(() => chartPeriodMap[chartPeriod.value]);

  const viewMode = ref<DetailViewMode>('multi-candidate');
  const selectedCandidateConid = ref<number | null>(null);

  // ── Single candidate details ──
  const { candidateDetails, isLoadingDetails, detailsError } = useCandidateDetails(selectedCandidateConid);

  // ── Derived state ──

  const filteredCandidates = computed(() => {
    if (!actualCandidates.value?.candidates) return null;
    const topTwo = filterTopTwoCandidates(
      actualCandidates.value.candidates,
      topOIData.value?.leading_dem_conid,
      topOIData.value?.leading_rep_conid,
    );
    return { candidates: topTwo, leading: topTwo[0] };
  });

  const hasSingleCandidate = computed(() => filteredCandidates.value?.candidates?.length === 1);

  // Gates the Prediction Markets "see more" CTA: only when the race's strike_count (all exchanges)
  // exceeds the top two shown; falls back to the returned contracts when strike_count is absent
  const hasMoreContracts = computed(() => {
    const strikeCount = topOIData.value?.strike_count ?? topOIData.value?.contracts?.length ?? 0;
    return strikeCount > (filteredCandidates.value?.candidates?.length ?? 0);
  });

  // Chart conids
  const chartConids = computed(() => {
    if (viewMode.value === 'single-candidate' && selectedCandidateConid.value) {
      return [selectedCandidateConid.value.toString()];
    }
    if (!filteredCandidates.value?.candidates) return [];
    return filteredCandidates.value.candidates.map((c) => c.conid.toString());
  });

  // Chart colors
  const chartColors = computed(() => {
    if (viewMode.value === 'single-candidate') {
      const candidate = actualCandidates.value?.candidates?.find((c) => c.conid === selectedCandidateConid.value);
      return candidate ? [getPartyColor(candidate.party)] : [PARTY_COLORS.NEUTRAL_DARK];
    }
    if (!filteredCandidates.value?.candidates) return [PARTY_COLORS.DEM_DARK, PARTY_COLORS.GOP_DARK];
    return filteredCandidates.value.candidates.map((c) => getPartyColor(c.party));
  });

  const activeProductConid = computed((): number | null => selectedProduct.value?.conid ?? null);

  // Badge color — uses API-provided party/color (intensity 0-3) when available
  const DEM_COLORS = [PARTY_COLORS.TOSS_UP, PARTY_COLORS.DEM_LIGHT, PARTY_COLORS.DEM_MID, PARTY_COLORS.DEM_DARK];
  const GOP_COLORS = [PARTY_COLORS.TOSS_UP, PARTY_COLORS.GOP_LIGHT, PARTY_COLORS.GOP_MID, PARTY_COLORS.GOP_DARK];
  const GRN_COLORS = [PARTY_COLORS.TOSS_UP, PARTY_COLORS.GRN_LIGHT, PARTY_COLORS.GRN_MID, PARTY_COLORS.GRN_DARK];

  const badgeColor = computed(() => {
    const partyValue = toValue(options.party);
    const colorValue = toValue(options.color);

    if (partyValue && colorValue != null) {
      const idx = Math.max(0, Math.min(colorValue, 3));
      const lower = partyValue.toLowerCase();
      if (lower.includes('democrat')) return DEM_COLORS[idx];
      if (lower.includes('republican')) return GOP_COLORS[idx];
      if (lower.includes('green')) return GRN_COLORS[idx];
      return PARTY_COLORS.IND_DARK;
    }

    return PARTY_COLORS.TOSS_UP;
  });

  // ── Actions ──

  function handleCandidateSelect(conid: number) {
    selectedCandidateConid.value = conid;
    viewMode.value = 'single-candidate';
  }

  function handleBackToMultiView() {
    selectedCandidateConid.value = null;
    viewMode.value = 'multi-candidate';
  }

  function handleCandidatePhotoClick(conid: number) {
    if (!hasSingleCandidate.value) {
      handleCandidateSelect(conid);
    }
  }

  // ── Watchers ──

  // Reset state when panel opens
  watch(
    () => toValue(visible),
    (isVisible) => {
      if (isVisible) {
        chartMode.value = 'line';

        const initCandidate = options.initialCandidateConid ? toValue(options.initialCandidateConid) : undefined;

        if (initCandidate) {
          selectedCandidateConid.value = initCandidate;
          viewMode.value = 'single-candidate';
        } else {
          viewMode.value = 'multi-candidate';
          selectedCandidateConid.value = null;
        }
      }
    },
    { immediate: true },
  );

  // Deferred candidate selection: apply initial candidate once data loads
  watch(
    () => actualCandidates.value?.candidates,
    (candidates) => {
      const initCandidate = options.initialCandidateConid ? toValue(options.initialCandidateConid) : undefined;

      if (
        initCandidate &&
        candidates?.length &&
        viewMode.value === 'single-candidate' &&
        selectedCandidateConid.value === initCandidate
      ) {
        const found = candidates.find((c) => c.conid === initCandidate);
        if (!found) {
          viewMode.value = 'multi-candidate';
          selectedCandidateConid.value = null;
        }
      }
    },
    { immediate: true },
  );

  return {
    // Product selector
    selectedProduct,
    availableProducts,
    topOIData,
    electionContract,
    actualCandidates,
    productConids,
    isLoadingContracts,
    contractError,
    marketInfo,

    // UI state
    chartMode,
    chartModeOptions,
    chartPeriod,
    chartPeriodOptions,
    chartPeriodConfig,
    viewMode,
    selectedCandidateConid,

    // Single candidate
    candidateDetails,
    isLoadingDetails,
    detailsError,

    // Derived
    filteredCandidates,
    hasSingleCandidate,
    hasMoreContracts,
    chartConids,
    chartColors,
    activeProductConid,
    badgeColor,

    // Actions
    handleCandidateSelect,
    handleBackToMultiView,
    handleCandidatePhotoClick,

    // Re-export helper for sub-components
    getPartyColor,
  };
}
